import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Button, EmptyState, Icon, StateNotice, Status } from "@snowiespace/ui/web";
import { client } from "./api.js";
import { useSpaceEventStatus } from "./events.js";
import { ui } from "./styles.js";

function statePresentation(state: string): { label: string; tone: "neutral" | "positive" | "warning" } {
  if (state === "active") return { label: "Active", tone: "positive" };
  if (state === "waiting_input") return { label: "Needs input", tone: "warning" };
  if (state === "pending_feedback") return { label: "Needs review", tone: "warning" };
  if (state === "failed") return { label: "Failed", tone: "warning" };
  return { label: "Sleeping", tone: "neutral" };
}

function targetLabel(target?: { kind: string; id: string }): string {
  if (!target) return "General Space conversation";
  return `${target.kind === "page" ? "Page" : target.kind === "file" ? "Desk file" : target.kind} · ${target.id}`;
}

export function SnowieStatus() {
  const { spaceId } = useParams({ from: "/spaces/$spaceId/snowie" });
  const navigate = useNavigate();
  const eventStatus = useSpaceEventStatus();
  const conversations = useQuery({
    queryKey: ["snowie", "conversations", spaceId],
    queryFn: () => client.conversations(spaceId),
    refetchInterval: 2_000,
  });
  const profile = useQuery({ queryKey: ["snowie", "profile", spaceId], queryFn: () => client.snowieProfile(spaceId) });
  const sessions = [...(conversations.data?.conversations ?? [])].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  const active = sessions.filter((session) => session.state === "active");
  const waiting = sessions.filter((session) => session.state === "waiting_input" || session.state === "pending_feedback");
  const provider = profile.data?.availableProviders.find((candidate) => candidate.id === profile.data?.profile.providerProfile);
  const apiKeyRequired = Boolean(provider?.spaceApiKeySupported && profile.data?.profile.credentialSource !== "space");

  return <div className={ui("snowie-status-page")} data-scroll-owner="snowie-status">
    <header className={ui("snowie-status-heading")}>
      <div><span className={ui("ss-eyebrow")}>Space Snowie</span><h1>Status and sessions</h1><p>See what Snowie is doing now and jump back into any durable Space session.</p></div>
      <Button icon="poke" onPress={() => void navigate({ to: "/spaces/$spaceId/poke", params: { spaceId }, search: { targetKind: undefined, targetId: undefined, conversationId: undefined, jobId: undefined } })}>Open Poke</Button>
    </header>

    {eventStatus !== "live" ? <StateNotice kind="reconnect" title="Status is reconnecting">Durable session truth is replaying before new activity appears.</StateNotice> : null}
    {apiKeyRequired ? <StateNotice kind="validation" title="API key required">Add a Space-owned provider key in Snowie Settings before starting another Job.</StateNotice> : null}

    <section className={ui("snowie-status-summary")} aria-label="Snowie status summary">
      <article><Icon name="snowie" /><strong>{active.length}</strong><span>running now</span><small>Concurrent executing sessions in this Space</small></article>
      <article><Icon name="warning" /><strong>{waiting.length}</strong><span>need you</span><small>Waiting for input or candidate review</small></article>
      <article><Icon name="history" /><strong>{sessions.length}</strong><span>total sessions</span><small>Durable conversations retained by the kernel</small></article>
      <article><Icon name="settings" /><strong>{provider?.displayName ?? profile.data?.profile.providerProfile ?? "Loading"}</strong><span>provider</span><small>Applies to future Jobs in this Space</small></article>
    </section>

    <section className={ui("snowie-session-directory")}>
      <header><div><span className={ui("ss-eyebrow")}>All sessions</span><h2>Open workrooms</h2></div><Status tone={apiKeyRequired ? "warning" : active.length ? "positive" : "neutral"}>{apiKeyRequired ? "API key required" : active.length ? `${active.length} active` : "Ready"}</Status></header>
      {sessions.length ? <div className={ui("snowie-session-list")}>{sessions.map((session) => {
        const state = statePresentation(session.state);
        return <Link
          className={ui("snowie-session-link")}
          key={session.id}
          params={{ spaceId }}
          search={{ targetKind: session.target?.kind, targetId: session.target?.id, conversationId: session.id, jobId: session.latestJobId }}
          to="/spaces/$spaceId/poke"
        >
          <span className={ui("snowie-session-icon")}><Icon name="snowie" /></span>
          <span><strong>{session.title || "Untitled session"}</strong><small>{targetLabel(session.target)} · updated {new Date(session.updatedAt).toLocaleString()}</small></span>
          <Status tone={state.tone}>{state.label}</Status><Icon name="chevron-right" />
        </Link>;
      })}</div> : !conversations.isLoading ? <EmptyState icon="snowie" title="No Snowie sessions yet">Start one bounded outcome from Poke, Page, or Desk.</EmptyState> : <p className={ui("muted")} role="status">Loading durable sessions…</p>}
    </section>
  </div>;
}
